import React, { useState, useEffect, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import styled, { keyframes } from "styled-components";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import FindInPageIcon from "@material-ui/icons/FindInPage";
import DescriptionIcon from "@material-ui/icons/Description";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import SortIcon from "@material-ui/icons/SortRounded";
import Chip from "@material-ui/core/Chip";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import Tooltip from "@material-ui/core/Tooltip";
import ApiClient from "utils/ApiClient";
import { FixedSizeList as VirtualList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    min-width: 600px;
    max-width: 900px;
    border-radius: 16px;
    animation: ${scaleIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #237ff4;
  border-radius: 12px 12px 0 0;
  position: relative;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    flex: 1;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 20px 24px 24px 24px;
  background-color: white;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  
  .MuiTextField-root {
    .MuiOutlinedInput-root {
      border-radius: 12px;
      background-color: #f8f9fa;
      border: 2px solid transparent;
      transition: all 0.2s ease;
      font-size: 0.95rem;
      
      &:hover {
        background-color: #f1f3f4;
        border-color: #e3f2fd;
      }
      
      &.Mui-focused {
        background-color: white;
        border-color: #237ff4;
        box-shadow: 0 0 0 3px rgba(35, 127, 244, 0.1);
      }
      
      .MuiOutlinedInput-notchedOutline {
        border: none;
      }
      
      .MuiInputBase-input {
        padding: 14px 16px;
        color: #333;
        
        &::placeholder {
          color: #999;
          opacity: 1;
        }
      }
    }
  }
`;

const SheetsList = styled(List)`
  flex: 1;
  overflow: hidden;
  padding: 0;
  min-height: 0;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const StyledListItem = styled(ListItem)`
  border-radius: 8px;
  margin-bottom: 4px;
  cursor: pointer;
  padding: 12px 16px;
  transition: background-color 0.15s ease;
  
  &:hover {
    background-color: #f8f9fa;    
    .MuiListItemIcon-root svg {
        transform: scale(1.2);
    }
  }

  .MuiListItemIcon-root svg {
    transition: transform 0.2s ease-out;
  }
  
  .MuiListItemIcon-root {
    min-width: 40px;
    
    svg {
      font-size: 20px;
    }
  }
  
  .MuiListItemText-root {
    .MuiListItemText-primary {
      font-weight: 500;
      color: #333;
      font-size: 0.95rem;
    }
    
    .MuiListItemText-secondary {
      color: #666;
      font-size: 0.85rem;
      margin-top: 2px;
    }
  }
`;

const fadeInGlow = keyframes`
  from {
    box-shadow: 0 0 0 rgba(35,127,244,0);
  }
  to {
    box-shadow: 0 4px 15px rgba(35,127,244,0.2);
  }
`;
const NavbarButton = styled(Button)`
  color: white !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  padding: 8px 22px !important;
  border-radius: 50px !important;
  height: 40px !important;
  min-height: 40px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  position: relative;
  overflow: hidden;

  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: none !important;
  margin-right: 15px !important;

  transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, color 0.3s ease, margin-right 0.3s ease !important;

  .MuiButton-startIcon {
    margin-right: 6px !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease !important; /* Icon transform only */
  }

  .MuiButton-startIcon svg {
    font-size: 20px !important;
    color: white !important;
  }

  &:hover {
    background: linear-gradient(90deg, #237ff4 0%, #3498db 100%) !important;
    border-color: transparent !important;
    color: white !important;
    
    animation: ${fadeInGlow} 0.3s ease forwards;

    .MuiButton-startIcon {
      transform: scale(1.1) rotate(-10deg);
    }
    transform: none !important; 
  }

  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 4px rgba(52, 152, 219, 0.3) !important;
  }
`;

const CloseButton = styled(IconButton)`
  && {
    padding: 8px;
    color: #888 !important;
    transition: all 0.2s ease;
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.15) !important;
      color: white !important;
    }
    
    svg {
      font-size: 20px;
      color: white !important;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #777;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background-image: radial-gradient(#f1f3f4 1px, transparent 1px);
  background-size: 15px 15px;
  border-radius: 8px;
  margin: 10px 0;

  .empty-icon {
    font-size: 48px;
    color: #237ff4;
    opacity: 0.5;
    margin-bottom: 16px;
  }
  
  .empty-text {
    font-size: 1.1rem;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }
  
  .empty-subtext {
    font-size: 0.9rem;
    color: #888;
    max-width: 300px;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  
  .loading-text {
    font-size: 1rem;
    margin-top: 16px;
  }
`;

const NavigationOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(1px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 12px;
  
  .loading-content {
    text-align: center;
    color: #666;
    
    .loading-text {
      font-size: 1rem;
      margin-top: 16px;
      font-weight: 500;
    }
  }
`;

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const SkeletonRow = styled.div`
  display:flex;
  align-items:center;
  gap:12px;
  padding:12px 14px;
  border-radius: 10px;
  margin-bottom: 8px;
  background: linear-gradient(180deg, rgba(0,0,0,0.01), rgba(0,0,0,0.01));
  position: relative;
  overflow: hidden;
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #eeeeee 25%, #f6f7f8 50%, #eeeeee 75%);
  background-size: 900px 100%;
  animation: ${shimmer} 1.3s linear infinite;
  border-radius: 8px;
  &.avatar { width: 44px; height: 44px; border-radius: 10px; flex: 0 0 44px; position: relative; overflow: hidden; display:flex; align-items:center; justify-content:center; }
  &.title { height: 14px; width: 48%; border-radius:6px; margin-bottom:8px; }
  &.subtitle { height: 12px; width: 32%; border-radius:6px; opacity:0.9; }
  @media (prefers-reduced-motion: reduce) { animation: none; background: #eee; }
`;

const AvatarInitials = styled.div`
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:700;
  color: rgba(255,255,255,0.95);
  font-size: 14px;
  letter-spacing: 0.6px;
  background: linear-gradient(135deg, rgba(35,127,244,0.9), rgba(52,152,219,0.95));
  transform: translateY(6px);
  opacity: 0;
  transition: transform 300ms ease, opacity 260ms ease;
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  
  .MuiChip-root {
    font-weight: 600;
    border-radius: 16px;
    border-width: 1.5px;
    border-color: #777;
  }

  .MuiIconButton-root {
    border: 1.5px solid #777;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;

  .sort-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .sort-icon {
    color: #000;
  }

  .MuiTextField-root {
    flex-grow: 1;
    
    .MuiOutlinedInput-root {
      border-radius: 12px;
      background-color: #f8f9fa;
      border: 2px solid transparent;
      transition: all 0.2s ease;
      font-size: 0.95rem;
      
      &:hover {
        background-color: #f1f3f4;
        border-color: #e3f2fd;
      }
      
      &.Mui-focused {
        background-color: white;
        border-color: #237ff4;
        box-shadow: 0 0 0 3px rgba(35, 127, 244, 0.1);
      }
      
      .MuiOutlinedInput-notchedOutline {
        border: none;
      }
      
      .MuiInputBase-input {
        padding: 14px 16px;
        color: #333;
        
        &::placeholder {
          color: #999;
          opacity: 1;
        }
      }
    }
  }
`;

const MyTigersheets = observer(({ store }) => {
  const [open, setOpen] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [filteredSheets, setFilteredSheets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'name', 
    direction: 'ascending' 
  });
  const searchRef = useRef(null);
  
  useEffect(() => {
    if (open && !loading && searchRef.current) {
      searchRef.current.focus();
      if (typeof searchRef.current.select === "function") {
        searchRef.current.select();
      }
    }
  }, [open, loading]);

  const handleOpen = () => {
    setOpen(true);
    fetchSheets();
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
    setSortConfig({ key: 'name', direction: 'ascending' });
  };

  const sortedAndFilteredSheets = useMemo(() => {
    const sortableItems = [...filteredSheets]; // Create a new array
    
    sortableItems.sort((a, b) => {
      const isAscending = sortConfig.direction === 'ascending';
      
      if (sortConfig.key === 'name') {
        return isAscending 
          ? a.name.localeCompare(b.name) 
          : b.name.localeCompare(a.name);
      }
      
      if (sortConfig.key === 'created_at') {
        return isAscending
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      }
      
      return 0;
    });
  
    return sortableItems;
  }, [filteredSheets, sortConfig]);

  const Row = ({ index, style }) => {
    const sheet = sortedAndFilteredSheets[index];
    return (
      <div style={style} key={sheet.id}>
        {renderSheetItem(sheet)}
      </div>
    );
  };

  const handleSortKeyChange = (key) => {
    setSortConfig({ key, direction: 'ascending' });
  };

  const toggleSortDirection = () => {
    setSortConfig(prevConfig => ({
      ...prevConfig,
      direction: prevConfig.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const fetchSheets = async () => {
    try {
      setLoading(true);
      if(store.appStore && store.appStore.allUserLists && Array.isArray(store.appStore.allUserLists) && store.appStore.allUserLists.length > 0 && !store.appStore.shouldFetchUserLists) {
        setSheets(store.appStore.allUserLists);
        setFilteredSheets(store.appStore.allUserLists);
      } else {
        const response = await ApiClient.Items.getUserLists({
          is_compact: true
        });
        if (response.success) {
          if(store.appStore) {
            store.appStore.allUserLists = response.data;
            store.appStore.shouldFetchUserLists = false;
          }
          setSheets(response.data || []);
          setFilteredSheets(response.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching sheets:", error);
      store?.appStore?.notificationStore?.showNotification(
        "Error loading sheets",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredSheets(sheets);
      return;
    }

    const filtered = sheets.filter(sheet => 
      sheet.name?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredSheets(filtered);
  };

  const handleSheetClick = async (sheet) => {
    try {
      setIsNavigating(true);
      if(sheet.is_default === '1' || sheet.is_default === 1) {
        store?.appStore?.routerHistory?.push(`/pack/system/list/${sheet.id}`);
        handleClose();
        return;
      }
      const response = await ApiClient.Packs.getPackBySheetId({
        sheet_id: sheet.id
      });
      
      if (response.success && response.data && response.data?.pack_id) {
        // if(response.data?.default_pack && response.data?.sheetData && store?.appStore?.leftHoverMenuStore) {
        //   if(!store?.appStore?.leftHoverMenuStore?.packs?.some(pack => pack.id == response.data.default_pack.id)) {
        //     await store?.appStore?.leftHoverMenuStore.loadPacks(false);
        //   }
        //   store.appStore.leftHoverMenuStore.packLists = [...store.appStore.leftHoverMenuStore.packLists, response.data.sheetData];
        //   // store?.appStore?.leftHoverMenuStore?.packs.unshift({ ...response.data.default_pack, is_new: true, is_admin: true });
        // }
        if(store?.appStore?.leftHoverMenuStore) {
          const lhm = store.appStore.leftHoverMenuStore;
          const packId = response.data.pack_id;
          const packExists = Array.isArray(lhm.packs) && lhm.packs.some(pack => String(pack.id) === String(packId));
          if (!packExists) {
            await lhm.loadPacks(false);
          }
          if (response.data?.default_pack && response.data?.sheetData) {
            const existing = Array.isArray(lhm.packLists) ? lhm.packLists : [];
            const sheetId = response.data.sheetData?.id;
            const hasSheet = existing.some(s => String(s?.id) === String(sheetId));
            if (!hasSheet) {
              lhm.packLists = [...existing, response.data.sheetData];
            }
          } else {
            const allPacks = lhm.packs;
            const targetPackId = response.data.pack_id;
            const targetPack = allPacks.find(pack => String(pack.id) === String(targetPackId));
            if (targetPack && targetPack.lists && Array.isArray(targetPack.lists)) {
              const sheetIdExists = targetPack.lists.map(String).includes(String(sheet.id));
              if (!sheetIdExists) {
                targetPack.lists = [...targetPack.lists, sheet.id];
                await lhm.loadPackLists(targetPackId);
              }
            }
          }
        }
        
        store?.appStore?.routerHistory?.push(`/pack/${response.data.pack_id}/list/${sheet.id}`);
        if(store?.appStore?.leftHoverMenuStore) {
          store?.appStore?.leftHoverMenuStore?.addToRecentPacks(response.data.pack_id);
        }
        handleClose();
      } else {
        store?.appStore?.notificationStore?.showNotification(
          "Unable to find the pack for this sheet",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching pack for sheet:", error);
      store?.appStore?.notificationStore?.showNotification(
        "Error loading sheet",
        "error"
      );
    } finally {
      setIsNavigating(false);
    }
  };

  const renderSkeletons = () => {
    return Array.from({ length: 4 }).map((_, i) => {
      const delay = i * 70;
      return (
        <SkeletonRow key={`s-${i}`} style={{ animationDelay: `${delay}ms` }}>
          <SkeletonBlock className="avatar" style={{ animationDelay: `${delay}ms` }}>
            <AvatarInitials style={{ transitionDelay: `${40 + delay}ms`, transform: 'translateY(6px)', opacity: 0 }} aria-hidden>
            </AvatarInitials>
          </SkeletonBlock>

          <div style={{ flex: 1 }}>
            <SkeletonBlock className="title" style={{ animationDelay: `${delay}ms` }} />
            <SkeletonBlock className="subtitle" style={{ animationDelay: `${delay + 30}ms` }} />
          </div>

          <div style={{ width: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SkeletonBlock style={{ width: 22, height: 22, borderRadius: 6, animationDelay: `${delay + 20}ms` }} />
          </div>
        </SkeletonRow>
      );
    });
  };

  const renderSheetItem = (sheet) => {
    const IconComponent = DescriptionIcon;
    return (
      <StyledListItem
        key={sheet.id}
        onClick={() => !isNavigating && handleSheetClick(sheet)}
        button
        style={{ 
          opacity: isNavigating ? 0.5 : 1,
          pointerEvents: isNavigating ? 'none' : 'auto'
        }}
      >
        <ListItemIcon>
          <IconComponent style={{ color: '#237ff4', fontSize: '22px' }} />
        </ListItemIcon>
        <ListItemText
          primary={sheet.name}
        />
      </StyledListItem>
    );
  };

  return (
    <>
      <NavbarButton
        variant="text"
        onClick={handleOpen}
        startIcon={<FindInPageIcon />}
        disableRipple
      >
        My Tigersheets
      </NavbarButton>

      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="my-tigersheets-dialog-title"
        PaperProps={{
          style: {
            height: '500px'
          },
        }}
        TransitionProps={{ onEntered: () => {
          if (open && !loading && searchRef.current) {
            searchRef.current.focus();
            if (typeof searchRef.current.select === "function") {
              searchRef.current.select();
            }
          }
        }}}
      >
        <StyledDialogTitle>
          <h2>My Tigersheets</h2>
          <CloseButton
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </CloseButton>
        </StyledDialogTitle>

        <StyledDialogContent style={{ position: 'relative' }}>
          {/* <SearchContainer>
            <TextField
              fullWidth
              placeholder = {loading ? "Loading sheets..." : "Search sheets by name..."}
              value={searchTerm}
              onChange={handleSearch}
              inputRef={(el) => { searchRef.current = el; }}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </SearchContainer> */}
          <HeaderContainer>
            <TextField
              fullWidth
              placeholder = {loading ? "Loading sheets..." : "Search sheets by name..."}
              value={searchTerm}
              onChange={handleSearch}
              inputRef={(el) => { searchRef.current = el; }}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
            <SortContainer>
              <SortIcon className="sort-icon" />
              <Chip
                label="Name"
                clickable
                variant={sortConfig.key === 'name' ? 'default' : 'outlined'}
                color={sortConfig.key === 'name' ? 'primary' : 'default'}
                onClick={() => handleSortKeyChange('name')}
              />
              <Chip
                label="Date Created"
                clickable
                variant={sortConfig.key === 'created_at' ? 'default' : 'outlined'}
                color={sortConfig.key === 'created_at' ? 'primary' : 'default'}
                onClick={() => handleSortKeyChange('created_at')}
              />
              <Tooltip 
                title={sortConfig.direction === 'ascending' ? 'Sort Descending' : 'Sort Ascending'} 
                arrow
              >
                <IconButton size="small" onClick={toggleSortDirection}>
                  {sortConfig.direction === 'ascending' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </SortContainer>
          </HeaderContainer>

          <SheetsList aria-busy={loading}>
            {loading ? (
              <>
                {renderSkeletons()}
              </>
            ) : sortedAndFilteredSheets.length === 0 ? (
              <EmptyState>
                <div className="empty-icon">📄</div>
                <div className="empty-text">
                  {searchTerm ? 'No sheets found matching your search.' : 'No sheets available.'}
                </div>
                <div className="empty-subtext">
                  {searchTerm ? 'Try adjusting your search terms.' : 'Create your first sheet to get started.'}
                </div>
              </EmptyState>
            ) : (
              <AutoSizer>
                {({ height, width }) => (
                  <VirtualList
                    height={height}
                    itemCount={sortedAndFilteredSheets.length}
                    itemSize={50}
                    width={width}
                    overscanCount={6}
                  >
                    {Row}
                  </VirtualList>
                )}
              </AutoSizer>
            )}
          </SheetsList>

          {isNavigating && (
            <NavigationOverlay>
              <div className="loading-content">
                <CircularProgress size={32} style={{ color: '#237ff4' }} />
                <div className="loading-text">Opening sheet...</div>
              </div>
            </NavigationOverlay>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
});

export default MyTigersheets;
